import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import React, { forwardRef, memo, useRef, useState } from 'react';
import { css, jsx } from '@emotion/react';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import __noop from '@atlaskit/ds-lib/noop';
import GlobalTheme from '@atlaskit/theme/components';
import { getColors } from '../internal/colors';
import EllipsisItem from './ellipsis-item';
import { useOnRevealed } from './internal/use-on-revealed';
var defaultMaxItems = 8;
var defaultBreadcrumbsLabel = 'Breadcrumbs';
var defaultEllipsisLabel = 'Show more breadcrumbs';
var toArray = React.Children.toArray;
var analyticsAttributes = {
  componentName: 'breadcrumbs',
  packageName: "@atlaskit/breadcrumbs",
  packageVersion: "11.7.0"
};
var noop = __noop;
var interactiveElementSelector = 'a, button, [tabindex]:not([tabindex="-1"])';
var breadcrumbStyles = css({
  display: 'flex',
  margin: 0,
  padding: 0,
  flexWrap: 'wrap'
});
var InnerBreadcrumbs = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$defaultExpande = props.defaultExpanded,
      defaultExpanded = _props$defaultExpande === void 0 ? false : _props$defaultExpande,
      isExpanded = props.isExpanded,
      _props$maxItems = props.maxItems,
      maxItems = _props$maxItems === void 0 ? defaultMaxItems : _props$maxItems,
      _props$itemsBeforeCol = props.itemsBeforeCollapse,
      itemsBeforeCollapse = _props$itemsBeforeCol === void 0 ? 1 : _props$itemsBeforeCol,
      _props$itemsAfterColl = props.itemsAfterCollapse,
      itemsAfterCollapse = _props$itemsAfterColl === void 0 ? 1 : _props$itemsAfterColl,
      _props$children = props.children,
      children = _props$children === void 0 ? [] : _props$children,
      testId = props.testId,
      _props$onExpand = props.onExpand,
      providedExpanse = _props$onExpand === void 0 ? noop : _props$onExpand,
      analyticsContext = props.analyticsContext,
      _props$mode = props.mode,
      mode = _props$mode === void 0 ? 'light' : _props$mode,
      _props$label = props.label,
      label = _props$label === void 0 ? defaultBreadcrumbsLabel : _props$label,
      _props$ellipsisLabel = props.ellipsisLabel,
      ellipsisLabel = _props$ellipsisLabel === void 0 ? defaultEllipsisLabel : _props$ellipsisLabel;

  var _useState = useState(defaultExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      expanded = _useState2[0],
      setExpanse = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isClickedBySpace = _useState4[0],
      setExpansionTrigger = _useState4[1];

  var wrapperRef = useRef(null);
  var isControlled = typeof isExpanded !== 'undefined';
  var isExpansionHandled = providedExpanse !== noop;
  var shouldExpand = isControlled ? isExpanded : expanded;

  var _getColors = getColors(mode),
      separatorColor = _getColors.separatorColor;

  var focusFirstRevealed = function focusFirstRevealed() {
    if (wrapperRef.current) {
      var listItems = _toConsumableArray(wrapperRef.current.querySelectorAll('li'));

      var interactiveElements = listItems.map(function (li) {
        return li.querySelector(interactiveElementSelector);
      });
      var elementToFocus = interactiveElements[itemsBeforeCollapse];
      var firstInteractiveElement = interactiveElements[0];

      if (elementToFocus) {
        elementToFocus.focus && elementToFocus.focus();
      } else if (firstInteractiveElement) {
        firstInteractiveElement.focus && firstInteractiveElement.focus();
      } else {
        wrapperRef.current.focus();
      }
    }

    setExpansionTrigger(false);
  };

  useOnRevealed(focusFirstRevealed, {
    isExpanded: shouldExpand,
    isDisabled: !isClickedBySpace
  });
  var handleExpansion = usePlatformLeafEventHandler(_objectSpread({
    fn: function fn(event, analyticsEvent) {
      if (!isControlled) {
        setExpanse(function (expanded) {
          return !expanded;
        });
      }

      if (isExpansionHandled && isControlled || !isControlled) {
        setExpansionTrigger(event.target === document.activeElement);
      }

      return providedExpanse(event, analyticsEvent);
    },
    action: 'expanded',
    analyticsData: analyticsContext
  }, analyticsAttributes));

  var renderItemsWithEllipsis = function renderItemsWithEllipsis() {
    var allItems = childrenArray; // This defends against someone passing weird data, to ensure that if all
    // items would be shown anyway, we just show all items without the EllipsisItem

    if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
      return allItems;
    }

    var beforeItems = allItems.slice(0, itemsBeforeCollapse);
    var afterItems = allItems.slice(allItems.length - itemsAfterCollapse, allItems.length);
    return [].concat(_toConsumableArray(beforeItems), [jsx(EllipsisItem, {
      key: "ellipsis",
      testId: testId && "".concat(testId, "--breadcrumb-ellipsis"),
      onClick: handleExpansion,
      label: ellipsisLabel
    })], _toConsumableArray(afterItems));
  };

  var childrenArray = toArray(children);
  var shouldDisplayItems = shouldExpand || maxItems && childrenArray.length <= maxItems;
  var breadcrumbsItems = shouldDisplayItems ? childrenArray : renderItemsWithEllipsis();
  return jsx("nav", {
    "aria-label": label,
    ref: mergeRefs([ref, wrapperRef]),
    tabIndex: -1
  }, jsx("ol", {
    "data-testid": testId,
    css: breadcrumbStyles,
    style: {
      color: separatorColor
    }
  }, breadcrumbsItems));
});
var Breadcrumbs = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function (props, ref) {
  return jsx(GlobalTheme.Consumer, null, function (tokens) {
    return jsx(InnerBreadcrumbs, _extends({}, props, {
      mode: tokens.mode,
      ref: ref
    }));
  });
})); // eslint-disable-next-line @repo/internal/react/require-jsdoc

export default Breadcrumbs;